import { useEffect, useMemo, useRef } from 'react';
import { useContent } from '../contexts/ContentContext';

type PlatformId = 'instagram' | 'facebook' | 'linkedin';

type PlatformConfig = {
  id: PlatformId;
  label: string;
  ctaFallback: string;
  brandClasses: {
    ring: string;
    accent: string;
    button: string;
    iconBg: string;
  };
  Icon: () => JSX.Element;
};

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
    <path
      fill="currentColor"
      d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm5 3.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm0 2a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zM18 6.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
    />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
    <path
      fill="currentColor"
      d="M13.5 22v-8h2.6l.4-3h-3V9.3c0-.9.3-1.5 1.6-1.5H17V5.1c-.3 0-1.2-.1-2.2-.1-2.2 0-3.7 1.3-3.7 3.8V11H9v3h2v8h2.5z"
    />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
    <path
      fill="currentColor"
      d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8h4V23h-4V8zm7.5 0h3.8v2.05h.05c.53-1 1.82-2.05 3.75-2.05 4 0 4.75 2.65 4.75 6.1V23h-4v-6.6c0-1.57-.03-3.6-2.2-3.6-2.2 0-2.54 1.7-2.54 3.49V23h-4V8z"
    />
  </svg>
);

const PLATFORMS: PlatformConfig[] = [
  {
    id: 'instagram',
    label: 'Instagram',
    ctaFallback: 'Apri su Instagram',
    brandClasses: {
      ring: 'ring-pink-500/40',
      accent: 'text-pink-400',
      button: 'bg-pink-600 hover:bg-pink-700 text-white',
      iconBg: 'bg-pink-500/15 text-pink-300',
    },
    Icon: InstagramIcon,
  },
  {
    id: 'facebook',
    label: 'Facebook',
    ctaFallback: 'Apri su Facebook',
    brandClasses: {
      ring: 'ring-blue-500/40',
      accent: 'text-blue-400',
      button: 'bg-blue-600 hover:bg-blue-700 text-white',
      iconBg: 'bg-blue-500/15 text-blue-300',
    },
    Icon: FacebookIcon,
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    ctaFallback: 'Apri su LinkedIn',
    brandClasses: {
      ring: 'ring-sky-500/40',
      accent: 'text-sky-400',
      button: 'bg-sky-600 hover:bg-sky-700 text-white',
      iconBg: 'bg-sky-500/15 text-sky-300',
    },
    Icon: LinkedInIcon,
  },
];

type CardData = PlatformConfig & {
  url: string;
  title: string;
  cta: string;
  thumb: string;
  embedCode?: string;
};

export const SocialHighlights = () => {
  const { getValue } = useContent();

  const cards: CardData[] = useMemo(() => {
    return PLATFORMS.map((p) => {
      const url = getValue(`social.${p.id}.latestUrl`, '');
      const title = getValue(`social.${p.id}.title`, p.label);
      const cta = getValue(`social.${p.id}.cta`, p.ctaFallback);
      const thumb = getValue(`social.${p.id}.thumbnail`, '');
      const embedCode = getValue(`social.${p.id}.embedCode`, '');
      return { ...p, url, title, cta, thumb, embedCode };
    });
  }, [getValue]);

  const anyActive = cards.some((c) => c.url && c.url.length > 0);

  // ——— Instagram embed support ———
  // Load the Instagram embed SDK once and process embeds when content changes.
  const igContainerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const hasInstagramEmbed = cards.some((c) => c.id === 'instagram' && c.embedCode && c.embedCode.trim().length > 0);
    if (!hasInstagramEmbed) return;

    const ensureScript = () =>
      new Promise<void>((resolve) => {
        if (typeof window !== 'undefined' && (window as any).instgrm?.Embeds) {
          resolve();
          return;
        }
        const existing = document.querySelector('script[data-ig-embed]') as HTMLScriptElement | null;
        if (existing) {
          existing.addEventListener('load', () => resolve());
          // In case it’s already loaded
          if ((window as any).instgrm?.Embeds) resolve();
          return;
        }
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.instagram.com/embed.js';
        script.setAttribute('data-ig-embed', 'true');
        script.onload = () => resolve();
        document.body.appendChild(script);
      });

    const processEmbeds = async () => {
      await ensureScript();
      try {
        (window as any).instgrm?.Embeds?.process();
      } catch {
        // ignore
      }
    };

    void processEmbeds();
  }, [cards]);

  const sanitizeEmbed = (html: string) => {
    // Strip any <script> tags for safety; the SDK is loaded separately
    return html.replace(/<\s*script[^>]*>[\s\S]*?<\s*\/\s*script\s*>/gi, '');
  };

  return (
    <section className="py-12 animate-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-extrabold">In evidenza dai social</h1>
          <p className="text-regai-muted mt-1">Ultimi contenuti pubblicati sui nostri canali.</p>
        </div>

        {!anyActive && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-sm text-gray-300">
            Nessun post in evidenza al momento. Aggiungi gli URL dalla sezione “Testi” dell’area admin.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card) => (
            <article
              key={card.id}
              className={`relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-5 ring-1 ${card.brandClasses.ring}`}
            >
              <div className="flex items-center justify-between">
                <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold ${card.brandClasses.iconBg}`}>
                  <card.Icon />
                  <span>{card.label}</span>
                </div>
              </div>

              <h2 className="mt-4 text-lg font-semibold text-white line-clamp-2 min-h-[2.5rem]">{card.title}</h2>

              {/* Render embed if provided (Instagram fully supported). Fallback to thumbnail panel. */}
              {card.embedCode && card.embedCode.trim().length > 0 ? (
                <div
                  ref={(el) => {
                    igContainerRefs.current[card.id] = el;
                  }}
                  className="mt-4"
                >
                  <div
                    className="w-full"
                    // Deliberate: embed HTML from trusted admin. Script tags are stripped.
                    dangerouslySetInnerHTML={{ __html: sanitizeEmbed(card.embedCode) }}
                  />
                </div>
              ) : (
                <div className="mt-4 relative aspect-[16/9] overflow-hidden rounded-xl border border-white/10 bg-[radial-gradient(circle_at_top,_#312e81_0%,_rgba(15,15,26,0.9)_100%)]">
                  {card.thumb ? (
                    <img
                      src={card.thumb}
                      alt={`${card.label} anteprima`}
                      className="absolute inset-0 h-full w-full object-cover opacity-80"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center">
                      <div className={`inline-flex items-center gap-2 rounded-lg px-3 py-1 text-xs ${card.brandClasses.iconBg}`}>
                        <card.Icon />
                        <span className="opacity-80">Anteprima</span>
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/10" aria-hidden="true" />
                </div>
              )}

              <div className="mt-5">
                <a
                  href={card.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center justify-center rounded-2xl px-4 py-2 font-semibold transition-colors ${card.brandClasses.button} ${!card.url ? 'pointer-events-none opacity-50' : ''}`}
                  aria-disabled={!card.url}
                >
                  {card.cta}
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialHighlights;
